import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    SettingsInputAntenna as RadioIcon,
    PlayArrow as PlayIcon,
    Save as SaveIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import ConfirmationDialog from './ConfirmationDialog';

const PWM_LO = 800;
const PWM_HI = 2200;
const PWM_SPAN = PWM_HI - PWM_LO;
const TOTAL_CHANNELS = 16;
// Channels typically used for sticks/throttle that should not auto-trim from spring-centered live value
// (we still allow user to confirm). For now we don't special-case throttle, just write current pwm as trim.

const pwmPct = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return null;
    const n = Number(v);
    return Math.max(0, Math.min(100, ((n - PWM_LO) / PWM_SPAN) * 100));
};

const getParamEntry = (mavId, name) => {
    if (!mavId || !window.paramList || !window.paramList[mavId]) return null;
    return window.paramList[mavId][name] || null;
};

const getParamValue = (mavId, name) => {
    const e = getParamEntry(mavId, name);
    return e ? e.value : null;
};

const isActivePwm = (pwm) => pwm != null && pwm !== 0 && pwm !== 65535;

const ROW_GRID = '60px 180px 80px 80px 80px minmax(160px, 1fr) 80px';

const ChannelRow = React.memo(({
    index, pwm, min, max, trim, calibrating, capMin, capMax,
    optionValue, optionEntries, optionDisabled, onChangeOption, theme,
}) => {
    const valNum = pwm == null ? null : Number(pwm);
    const inactive = !isActivePwm(pwm);
    const minPct = pwmPct(min);
    const maxPct = pwmPct(max);
    const trimPct = pwmPct(trim);
    const valPct = pwmPct(valNum);
    const capMinPct = pwmPct(capMin);
    const capMaxPct = pwmPct(capMax);

    const displayMin = calibrating && capMin != null ? capMin : min;
    const displayMax = calibrating && capMax != null ? capMax : max;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: ROW_GRID,
                alignItems: 'center',
                columnGap: 1.5,
                py: 0.5,
                px: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography variant="body2" sx={{ textAlign: 'center', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                CH{index}
            </Typography>
            {optionEntries ? (() => {
                const currentNum = Number(optionValue);
                const known = optionEntries.some((o) => o.value === currentNum);
                return (
                    <Select
                        size="small"
                        value={Number.isFinite(currentNum) ? currentNum : 0}
                        disabled={optionDisabled}
                        onChange={(e) => onChangeOption(index, Number(e.target.value))}
                        renderValue={(v) => {
                            const found = optionEntries.find((o) => o.value === Number(v));
                            return found ? `${found.label} (${found.value})` : `Unknown (${v})`;
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 360, fontSize: 12 } }, disableScrollLock: true }}
                        sx={{ width: '100%', fontSize: 12, '& .MuiSelect-select': { py: 0.25 } }}
                    >
                        {!known && Number.isFinite(currentNum) && (
                            <MenuItem value={currentNum} dense disabled>
                                Unknown ({currentNum})
                            </MenuItem>
                        )}
                        {optionEntries.map((o) => (
                            <MenuItem key={o.value} value={o.value} dense>
                                {o.label} ({o.value})
                            </MenuItem>
                        ))}
                    </Select>
                );
            })() : (
                <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.disabled' }}>—</Typography>
            )}
            <Typography
                variant="caption"
                sx={{
                    textAlign: 'center',
                    color: calibrating && capMin != null ? 'warning.main' : 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: calibrating && capMin != null ? 600 : 400,
                }}
            >
                {displayMin ?? '—'}
            </Typography>
            <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {trim ?? '—'}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    textAlign: 'center',
                    color: calibrating && capMax != null ? 'warning.main' : 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: calibrating && capMax != null ? 600 : 400,
                }}
            >
                {displayMax ?? '—'}
            </Typography>

            <Tooltip title={`${PWM_LO} — [min ${displayMin ?? '—'} / trim ${trim ?? '—'} / max ${displayMax ?? '—'}] — ${PWM_HI}`} placement="top" arrow>
                <Box sx={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center' }}>
                    {/* Rail */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        bgcolor: theme.palette.action.hover,
                        borderRadius: 1,
                    }} />
                    {/* saved min-max range stripe */}
                    {minPct != null && maxPct != null && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${Math.min(minPct, maxPct)}%`,
                            width: `${Math.abs(maxPct - minPct)}%`,
                            top: 4, bottom: 4,
                            bgcolor: theme.palette.action.selected,
                            borderRadius: 0.5,
                        }} />
                    )}
                    {/* calibration captured range overlay */}
                    {calibrating && capMinPct != null && capMaxPct != null && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${Math.min(capMinPct, capMaxPct)}%`,
                            width: `${Math.abs(capMaxPct - capMinPct)}%`,
                            top: 2, bottom: 2,
                            border: `1px solid ${theme.palette.warning.main}`,
                            borderRadius: 0.5,
                            bgcolor: 'transparent',
                        }} />
                    )}
                    {/* current value fill */}
                    {!inactive && minPct != null && valPct != null && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${Math.min(minPct, valPct)}%`,
                            width: `${Math.abs(valPct - minPct)}%`,
                            top: 4, bottom: 4,
                            bgcolor: theme.palette.success.main,
                            opacity: 0.75,
                            borderRadius: 0.5,
                        }} />
                    )}
                    {/* min/max vertical cursors */}
                    {minPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${minPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px solid ${theme.palette.text.disabled}`,
                        }} />
                    )}
                    {maxPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${maxPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px solid ${theme.palette.text.disabled}`,
                        }} />
                    )}
                    {/* trim cursor */}
                    {trimPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${trimPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px dashed ${theme.palette.warning.main}`,
                        }} />
                    )}
                </Box>
            </Tooltip>

            <Typography
                variant="caption"
                sx={{
                    textAlign: 'right',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontVariantNumeric: 'tabular-nums',
                    color: inactive ? 'text.disabled' : 'text.primary',
                    pr: 1,
                }}
            >
                {inactive ? '—' : valNum}
            </Typography>
        </Box>
    );
});

const FLTMODE_SLOTS = [
    { i: 1, label: 'PWM ≤ 1230' },
    { i: 2, label: '1231 – 1360' },
    { i: 3, label: '1361 – 1490' },
    { i: 4, label: '1491 – 1620' },
    { i: 5, label: '1621 – 1749' },
    { i: 6, label: 'PWM ≥ 1750' },
];

const fltmodeSlotFromPwm = (pwm) => {
    if (pwm == null || !Number.isFinite(Number(pwm))) return null;
    const v = Number(pwm);
    if (v <= 1230) return 1;
    if (v <= 1360) return 2;
    if (v <= 1490) return 3;
    if (v <= 1620) return 4;
    if (v <= 1749) return 5;
    return 6;
};

const Radio = ({ selectedMavId, mavTimestamp, paramListTimestamp, modeList }) => {
    const theme = useTheme();
    const [rcChannels, setRcChannels] = useState(null);
    const [rcChannelsRaw, setRcChannelsRaw] = useState(null);
    const [radioStatus, setRadioStatus] = useState(null);

    // Calibration state
    const [calibrating, setCalibrating] = useState(false);
    const captureRef = useRef({}); // { [i]: { min, max } }
    const [captureTick, setCaptureTick] = useState(0); // bump to force re-render
    const [startDialogOpen, setStartDialogOpen] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);

    useEffect(() => {
        if (!window.mav) return;
        const vehicle = window.mav[selectedMavId];
        if (!vehicle) return;
        if (vehicle.RC_CHANNELS) setRcChannels(vehicle.RC_CHANNELS);
        if (vehicle.RC_CHANNELS_RAW) setRcChannelsRaw(vehicle.RC_CHANNELS_RAW);
        if (vehicle.RADIO_STATUS) setRadioStatus(vehicle.RADIO_STATUS);
    }, [mavTimestamp, selectedMavId]);

    // Live capture loop
    useEffect(() => {
        if (!calibrating) return;
        let changed = false;
        for (let i = 1; i <= TOTAL_CHANNELS; i++) {
            let pwm = null;
            if (rcChannels && rcChannels[`chan${i}_raw`] !== undefined) {
                pwm = rcChannels[`chan${i}_raw`];
            } else if (rcChannelsRaw && rcChannelsRaw[`chan${i}_raw`] !== undefined) {
                pwm = rcChannelsRaw[`chan${i}_raw`];
            }
            if (!isActivePwm(pwm)) continue;
            const v = Number(pwm);
            if (!Number.isFinite(v) || v < PWM_LO || v > PWM_HI) continue;
            const cur = captureRef.current[i] || { min: v, max: v };
            const nextMin = Math.min(cur.min, v);
            const nextMax = Math.max(cur.max, v);
            if (nextMin !== cur.min || nextMax !== cur.max || !captureRef.current[i]) {
                captureRef.current[i] = { min: nextMin, max: nextMax };
                changed = true;
            }
        }
        if (changed) setCaptureTick((t) => t + 1);
    }, [calibrating, mavTimestamp, rcChannels, rcChannelsRaw]);

    const channels = useMemo(() => {
        const arr = [];
        for (let i = 1; i <= TOTAL_CHANNELS; i++) {
            let pwm = null;
            if (rcChannels && rcChannels[`chan${i}_raw`] !== undefined) {
                pwm = rcChannels[`chan${i}_raw`];
            } else if (rcChannelsRaw && rcChannelsRaw[`chan${i}_raw`] !== undefined) {
                pwm = rcChannelsRaw[`chan${i}_raw`];
            }
            const min = getParamValue(selectedMavId, `RC${i}_MIN`);
            const max = getParamValue(selectedMavId, `RC${i}_MAX`);
            const trim = getParamValue(selectedMavId, `RC${i}_TRIM`);
            const option = getParamValue(selectedMavId, `RC${i}_OPTION`);
            const cap = captureRef.current[i];
            arr.push({
                i, pwm, min, max, trim, option,
                capMin: cap ? cap.min : null,
                capMax: cap ? cap.max : null,
            });
        }
        return arr;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rcChannels, rcChannelsRaw, selectedMavId, paramListTimestamp, captureTick]);

    const activeCount = channels.filter((c) => isActivePwm(c.pwm)).length;
    const rcChanCount = rcChannels && rcChannels.chancount ? rcChannels.chancount : null;
    const rcRssi = rcChannels && rcChannels.rssi !== undefined ? rcChannels.rssi : null;

    // Build RC{i}_OPTION dropdown choices from metadata of the first defined RC*_OPTION.
    const optionEntries = useMemo(() => {
        if (!selectedMavId || !window.paramList || !window.paramList[selectedMavId]) return null;
        for (let i = 1; i <= TOTAL_CHANNELS; i++) {
            const e = window.paramList[selectedMavId][`RC${i}_OPTION`];
            if (e && e.metaData && e.metaData.values) {
                const values = e.metaData.values;
                const list = Object.keys(values).map((label) => ({
                    label,
                    value: Number(values[label]),
                })).filter((o) => Number.isFinite(o.value));
                list.sort((a, b) => a.value - b.value);
                return list;
            }
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMavId, paramListTimestamp]);

    const handleChangeOption = useCallback((index, newVal) => {
        const name = `RC${index}_OPTION`;
        const entry = getParamEntry(selectedMavId, name);
        if (!entry) return;
        window.api.setParams(selectedMavId, {
            [name]: { value: newVal, type: entry.type },
        });
    }, [selectedMavId]);

    // ---- Flight mode switch (FLTMODE_CH + FLTMODE1..6) ----
    const fltmodeChRaw = getParamValue(selectedMavId, 'FLTMODE_CH');
    const fltmodeCh = Number.isFinite(Number(fltmodeChRaw)) && Number(fltmodeChRaw) > 0
        ? Number(fltmodeChRaw)
        : null;

    const fltmodeChPwm = useMemo(() => {
        if (!fltmodeCh) return null;
        let pwm = null;
        if (rcChannels && rcChannels[`chan${fltmodeCh}_raw`] !== undefined) {
            pwm = rcChannels[`chan${fltmodeCh}_raw`];
        } else if (rcChannelsRaw && rcChannelsRaw[`chan${fltmodeCh}_raw`] !== undefined) {
            pwm = rcChannelsRaw[`chan${fltmodeCh}_raw`];
        }
        return isActivePwm(pwm) ? Number(pwm) : null;
    }, [fltmodeCh, rcChannels, rcChannelsRaw]);

    const activeFltmodeSlot = useMemo(() => fltmodeSlotFromPwm(fltmodeChPwm), [fltmodeChPwm]);

    const modeOptions = useMemo(() => {
        // Prefer FLTMODE1's parameter metadata: it lists every mode the
        // firmware advertises for this airframe (e.g. Plane firmware on a
        // QuadPlane build exposes all Q* modes here even though HEARTBEAT
        // reports MAV_TYPE_FIXED_WING and the per-vehicle modeList would
        // otherwise omit them).
        const byVal = new Map();
        const add = (value, label) => {
            const v = Number(value);
            if (!Number.isFinite(v)) return;
            if (!byVal.has(v)) byVal.set(v, { value: v, label: String(label) });
        };
        for (let i = 1; i <= 6; i++) {
            const entry = getParamEntry(selectedMavId, `FLTMODE${i}`);
            const values = entry && entry.metaData && entry.metaData.values;
            if (values) {
                Object.keys(values).forEach((label) => add(values[label], label));
            }
        }
        if (modeList) {
            Object.keys(modeList).forEach((k) => add(k, modeList[k]));
        }
        return Array.from(byVal.values()).sort((a, b) => a.value - b.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeList, selectedMavId, paramListTimestamp]);

    const fltmodeValues = useMemo(() => {
        const arr = [];
        for (let i = 1; i <= 6; i++) {
            const entry = getParamEntry(selectedMavId, `FLTMODE${i}`);
            arr.push({
                i,
                value: entry ? Number(entry.value) : null,
                entry,
            });
        }
        return arr;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMavId, paramListTimestamp]);

    const handleChangeFltmode = useCallback((i, newVal) => {
        const name = `FLTMODE${i}`;
        const entry = getParamEntry(selectedMavId, name);
        if (!entry) return;
        window.api.setParams(selectedMavId, {
            [name]: { value: newVal, type: entry.type },
        });
    }, [selectedMavId]);

    const handleChangeFltmodeCh = useCallback((newVal) => {
        const entry = getParamEntry(selectedMavId, 'FLTMODE_CH');
        if (!entry) return;
        window.api.setParams(selectedMavId, {
            FLTMODE_CH: { value: newVal, type: entry.type },
        });
    }, [selectedMavId]);

    const fltmodeChOptions = useMemo(() => {
        const entry = getParamEntry(selectedMavId, 'FLTMODE_CH');
        const values = entry && entry.metaData && entry.metaData.values;
        if (values) {
            const list = Object.keys(values).map((label) => ({
                label,
                value: Number(values[label]),
            })).filter((o) => Number.isFinite(o.value));
            list.sort((a, b) => a.value - b.value);
            if (list.length > 0) return list;
        }
        return [
            { label: 'Disabled', value: 0 },
            ...Array.from({ length: 11 }, (_, k) => ({ label: `Channel ${k + 5}`, value: k + 5 })),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMavId, paramListTimestamp]);

    const calibrationPlan = useMemo(() => {
        // Build list of channels that will be written on Save.
        const plan = [];
        for (let i = 1; i <= TOTAL_CHANNELS; i++) {
            const cap = captureRef.current[i];
            if (!cap) continue;
            const range = cap.max - cap.min;
            if (range < 200) continue; // ignore quiet / unused channels
            const curMinEntry = getParamEntry(selectedMavId, `RC${i}_MIN`);
            const curMaxEntry = getParamEntry(selectedMavId, `RC${i}_MAX`);
            const curTrimEntry = getParamEntry(selectedMavId, `RC${i}_TRIM`);
            // current live value as trim
            let liveTrim = null;
            if (rcChannels && rcChannels[`chan${i}_raw`] !== undefined) {
                liveTrim = rcChannels[`chan${i}_raw`];
            } else if (rcChannelsRaw && rcChannelsRaw[`chan${i}_raw`] !== undefined) {
                liveTrim = rcChannelsRaw[`chan${i}_raw`];
            }
            if (!isActivePwm(liveTrim)) liveTrim = null;
            plan.push({
                i,
                newMin: cap.min,
                newMax: cap.max,
                newTrim: liveTrim,
                curMin: curMinEntry ? curMinEntry.value : null,
                curMax: curMaxEntry ? curMaxEntry.value : null,
                curTrim: curTrimEntry ? curTrimEntry.value : null,
                minEntry: curMinEntry,
                maxEntry: curMaxEntry,
                trimEntry: curTrimEntry,
            });
        }
        return plan;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [captureTick, selectedMavId, paramListTimestamp, rcChannels, rcChannelsRaw]);

    const handleStart = useCallback(() => {
        captureRef.current = {};
        setCaptureTick((t) => t + 1);
        setCalibrating(true);
        setStartDialogOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        setCalibrating(false);
        captureRef.current = {};
        setCaptureTick((t) => t + 1);
    }, []);

    const handleSave = useCallback(() => {
        const updates = {};
        for (const p of calibrationPlan) {
            if (p.minEntry) {
                updates[`RC${p.i}_MIN`] = { value: p.newMin, type: p.minEntry.type };
            }
            if (p.maxEntry) {
                updates[`RC${p.i}_MAX`] = { value: p.newMax, type: p.maxEntry.type };
            }
            if (p.trimEntry && p.newTrim != null) {
                updates[`RC${p.i}_TRIM`] = { value: p.newTrim, type: p.trimEntry.type };
            }
        }
        if (Object.keys(updates).length > 0) {
            window.api.setParams(selectedMavId, updates);
        }
        setCalibrating(false);
        captureRef.current = {};
        setCaptureTick((t) => t + 1);
        setSaveDialogOpen(false);
    }, [calibrationPlan, selectedMavId]);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    overflow: 'hidden',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <RadioIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2">RC Input</Typography>
                    <Chip
                        size="small"
                        label={`${activeCount} active`}
                        color={activeCount > 0 ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                    {rcChanCount != null && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${rcChanCount} channels`}
                            sx={{ height: 20, fontSize: 11 }}
                        />
                    )}
                    {rcRssi != null && rcRssi !== 255 && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`RC RSSI ${Math.round((rcRssi / 254) * 100)}%`}
                            sx={{ height: 20, fontSize: 11 }}
                        />
                    )}
                    {radioStatus && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Link ${radioStatus.rssi} / Rem ${radioStatus.remrssi}`}
                            sx={{ height: 20, fontSize: 11 }}
                        />
                    )}

                    <Box sx={{ flex: 1 }} />

                    {!calibrating ? (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<PlayIcon />}
                            onClick={() => setStartDialogOpen(true)}
                            disabled={activeCount === 0}
                        >
                            Start Calibration
                        </Button>
                    ) : (
                        <>
                            <Chip
                                size="small"
                                color="warning"
                                label={`Calibrating · ${calibrationPlan.length} ch captured`}
                                sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={<CloseIcon />}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<SaveIcon />}
                                onClick={() => setSaveDialogOpen(true)}
                                disabled={calibrationPlan.length === 0}
                            >
                                Finish & Save
                            </Button>
                        </>
                    )}
                </Stack>

                {calibrating && (
                    <Typography variant="caption" sx={{ color: 'warning.main' }}>
                        Move all sticks and switches through their full range. Then center sticks/throttle to neutral position before clicking "Finish & Save".
                    </Typography>
                )}

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: ROW_GRID,
                            columnGap: 1.5,
                            px: 1,
                            py: 0.75,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            color: 'text.secondary',
                            position: 'sticky',
                            top: 0,
                            bgcolor: 'background.default',
                            zIndex: 1,
                        }}
                    >
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Channel</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Option</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Min</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Trim</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Max</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Input</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Value</Typography>
                    </Box>
                    {channels.map((c) => (
                        <ChannelRow
                            key={c.i}
                            index={c.i}
                            pwm={c.pwm}
                            min={c.min}
                            max={c.max}
                            trim={c.trim}
                            calibrating={calibrating}
                            capMin={c.capMin}
                            capMax={c.capMax}
                            optionValue={c.option}
                            optionEntries={optionEntries}
                            optionDisabled={calibrating || c.i <= 4 || c.i === fltmodeCh || !getParamEntry(selectedMavId, `RC${c.i}_OPTION`)}
                            onChangeOption={handleChangeOption}
                            theme={theme}
                        />
                    ))}
                </Box>

                {!rcChannels && !rcChannelsRaw && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Waiting for RC_CHANNELS…
                    </Typography>
                )}

                {/* Flight Modes (FLTMODE_CH + FLTMODE1..6) */}
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        p: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="subtitle2">Flight Modes</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>FLTMODE_CH</Typography>
                            <Select
                                size="small"
                                value={fltmodeCh ?? 0}
                                disabled={!getParamEntry(selectedMavId, 'FLTMODE_CH')}
                                onChange={(e) => handleChangeFltmodeCh(Number(e.target.value))}
                                renderValue={(v) => {
                                    const found = fltmodeChOptions.find((o) => o.value === Number(v));
                                    return found ? `${found.label} (${found.value})` : `Unknown (${v})`;
                                }}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 360, fontSize: 12 } }, disableScrollLock: true }}
                                sx={{ fontSize: 12, minWidth: 140, '& .MuiSelect-select': { py: 0.25 } }}
                            >
                                {fltmodeChOptions.map((o) => (
                                    <MenuItem key={o.value} value={o.value} dense>
                                        {o.label} ({o.value})
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        {fltmodeChPwm != null && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`PWM ${fltmodeChPwm}`}
                                sx={{ height: 20, fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
                            />
                        )}
                        {activeFltmodeSlot != null && (
                            <Chip
                                size="small"
                                color="success"
                                label={`Active: FLTMODE${activeFltmodeSlot}`}
                                sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                            />
                        )}
                        {modeOptions.length === 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Mode list unavailable
                            </Typography>
                        )}
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '110px 160px minmax(180px, 1fr)',
                            columnGap: 1.5,
                            rowGap: 0.5,
                            alignItems: 'center',
                        }}
                    >
                        {fltmodeValues.map((m) => {
                            const isActive = activeFltmodeSlot === m.i;
                            const known = modeOptions.some((o) => o.value === m.value);
                            const slot = FLTMODE_SLOTS.find((s) => s.i === m.i);
                            return (
                                <React.Fragment key={m.i}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                            fontWeight: isActive ? 700 : 400,
                                            color: isActive ? 'success.main' : 'text.primary',
                                        }}
                                    >
                                        FLTMODE{m.i}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: isActive ? 'success.main' : 'text.secondary',
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                        }}
                                    >
                                        {slot ? slot.label : ''}
                                    </Typography>
                                    <Select
                                        size="small"
                                        value={Number.isFinite(m.value) ? m.value : ''}
                                        disabled={!m.entry || modeOptions.length === 0}
                                        onChange={(e) => handleChangeFltmode(m.i, Number(e.target.value))}
                                        displayEmpty
                                        renderValue={(v) => {
                                            if (v === '' || v == null) return '—';
                                            const found = modeOptions.find((o) => o.value === Number(v));
                                            return found ? `${found.label} (${found.value})` : `Unknown (${v})`;
                                        }}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 360, fontSize: 12 } }, disableScrollLock: true }}
                                        sx={{
                                            width: '100%',
                                            fontSize: 12,
                                            '& .MuiSelect-select': { py: 0.25 },
                                            ...(isActive ? {
                                                outline: `1px solid ${theme.palette.success.main}`,
                                                borderRadius: 1,
                                            } : {}),
                                        }}
                                    >
                                        {!known && Number.isFinite(m.value) && (
                                            <MenuItem value={m.value} dense disabled>
                                                Unknown ({m.value})
                                            </MenuItem>
                                        )}
                                        {modeOptions.map((o) => (
                                            <MenuItem key={o.value} value={o.value} dense>
                                                {o.label} ({o.value})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            <ConfirmationDialog
                open={startDialogOpen}
                onClose={() => setStartDialogOpen(false)}
                onConfirm={handleStart}
                title="Start RC Calibration"
                description={
                    'Ensure the vehicle is disarmed and all propellers / motors are removed or disabled. ' +
                    'The transmitter must be powered on and bound. Continue?'
                }
                confirmColor="warning"
            />
            <ConfirmationDialog
                open={saveDialogOpen}
                onClose={() => setSaveDialogOpen(false)}
                onConfirm={handleSave}
                title="Save RC Calibration"
                description={
                    calibrationPlan.length === 0 ? (
                        'No channels captured a valid range. Nothing will be written.'
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2">
                                Write the following changes to {calibrationPlan.length} channel(s)?
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    bgcolor: 'background.default',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '60px 1fr 1fr 1fr',
                                        columnGap: 1,
                                        px: 1,
                                        py: 0.5,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        color: 'text.secondary',
                                        position: 'sticky',
                                        top: 0,
                                        bgcolor: 'background.default',
                                        zIndex: 1,
                                    }}
                                >
                                    <Typography variant="caption">CH</Typography>
                                    <Typography variant="caption">Min</Typography>
                                    <Typography variant="caption">Trim</Typography>
                                    <Typography variant="caption">Max</Typography>
                                </Box>
                                {calibrationPlan.map((p) => (
                                    <Box
                                        key={p.i}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '60px 1fr 1fr 1fr',
                                            columnGap: 1,
                                            px: 1,
                                            py: 0.5,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            fontVariantNumeric: 'tabular-nums',
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                            fontSize: 12,
                                        }}
                                    >
                                        <span>CH{p.i}</span>
                                        <span>
                                            {p.curMin ?? '—'} → <b>{p.newMin}</b>
                                        </span>
                                        <span>
                                            {p.curTrim ?? '—'} → <b>{p.newTrim ?? '—'}</b>
                                        </span>
                                        <span>
                                            {p.curMax ?? '—'} → <b>{p.newMax}</b>
                                        </span>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )
                }
                confirmColor="warning"
            />
        </Box>
    );
};

export default Radio;
